import { createTheme, responsiveFontSizes } from '@mui/material/styles';

// Define the color palette
const colors = {
  primary: {
    main: '#39FF14', // Neon green highlight color
    light: '#65FF4A',
    dark: '#2BCC10', // Dark green for secondary highlighting
    contrastText: '#121212',
  },
  secondary: {
    main: '#1E5631', // Dark green as secondary accent
    light: '#2A7A45',
    dark: '#0F3A1B',
    contrastText: '#FFFFFF',
  },
  background: {
    default: '#121212', // Dark background
    paper: '#1E1E1E',   // Dark gray for cards/elements
    elevated: '#2D2D2D', // Slightly lighter gray for elevated components
  },
  text: {
    primary: '#FFFFFF', // White for primary text
    secondary: '#B0B0B0', // Light gray for secondary text
    disabled: '#707070', // Medium gray for disabled text
  },
  divider: 'rgba(255, 255, 255, 0.12)',
  grid: {
    line: 'rgba(57, 255, 20, 0.2)', // CAD-like grid lines with neon green
    point: 'rgba(57, 255, 20, 0.5)', // CAD-like grid points with neon green
  },
};

// Create the base theme
let theme = createTheme({
  palette: {
    mode: 'dark', // Changed to dark mode
    primary: colors.primary,
    secondary: colors.secondary,
    background: {
      default: colors.background.default,
      paper: colors.background.paper,
    },
    text: colors.text,
    divider: colors.divider,
  },
  typography: {
    fontFamily: '"Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 600,
      fontSize: '3.5rem',
      letterSpacing: '0.02em',
    },
    h2: {
      fontWeight: 600,
      fontSize: '2.75rem',
      letterSpacing: '0.02em',
    },
    h3: {
      fontWeight: 600,
      fontSize: '2.25rem',
      letterSpacing: '0.02em',
    },
    h4: {
      fontWeight: 500,
      fontSize: '1.75rem',
      letterSpacing: '0.01em',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.5rem',
      letterSpacing: '0.01em',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.25rem',
      letterSpacing: '0.01em',
    },
    body1: {
      fontWeight: 400,
      fontSize: '1rem',
      letterSpacing: '0.01em',
      lineHeight: 1.7,
    },
    body2: {
      fontWeight: 400,
      fontSize: '0.875rem',
      letterSpacing: '0.01em',
      lineHeight: 1.7,
    },
    button: {
      fontWeight: 500,
      fontSize: '0.875rem',
      letterSpacing: '0.02em',
      textTransform: 'none',
    },
  },
  shape: {
    borderRadius: 2,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          textTransform: 'none',
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: 'none',
          },
        },
        containedPrimary: {
          '&:hover': {
            backgroundColor: colors.primary.light,
          },
        },
        containedSecondary: {
          '&:hover': {
            backgroundColor: colors.secondary.light,
          },
        },
        outlinedPrimary: {
          borderColor: colors.primary.main,
          '&:hover': {
            borderColor: colors.primary.light,
          },
        },
        outlinedSecondary: {
          borderColor: colors.secondary.main,
          '&:hover': {
            borderColor: colors.secondary.light,
          },
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.2)',
          borderBottom: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: colors.background.paper,
          borderRight: `1px solid ${colors.divider}`,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.2)',
          borderRadius: 3,
          overflow: 'hidden',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: colors.divider,
            },
            '&:hover fieldset': {
              borderColor: colors.primary.main,
            },
            '&.Mui-focused fieldset': {
              borderColor: colors.primary.main,
            },
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.paper,
          boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.2)',
          borderRadius: 2,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          backgroundColor: colors.background.elevated,
          borderRadius: 2,
        },
        colorPrimary: {
          backgroundColor: 'rgba(57, 255, 20, 0.15)',
          color: colors.primary.main,
        },
        colorSecondary: {
          backgroundColor: 'rgba(30, 86, 49, 0.15)',
          color: colors.secondary.light,
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          borderRadius: 2,
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderWidth: '2px',
            borderColor: colors.primary.main,
          },
        },
        notchedOutline: {
          borderColor: 'rgba(255, 255, 255, 0.15)',
          transition: '0.2s',
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${colors.divider}`,
          padding: '12px 16px',
        },
        head: {
          fontWeight: 600,
          backgroundColor: colors.background.elevated,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          '&.Mui-selected': {
            color: colors.primary.main,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: colors.primary.main,
          height: 3,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 3,
          boxShadow: '0px 8px 24px rgba(0, 0, 0, 0.12)',
        },
      },
    },
  },
});

// Make the typography responsive
theme = responsiveFontSizes(theme);

// Add custom values to the theme
theme = createTheme(theme, {
  custom: {
    colors: {
      ...colors,
    },
    transitions: {
      fast: '200ms',
      medium: '300ms',
      slow: '500ms',
    },
    spacing: {
      section: theme.spacing(10),
      page: theme.spacing(3),
    },
  },
});

// Extend the Material-UI theme interface
declare module '@mui/material/styles' {
  interface Theme {
    custom: {
      colors: typeof colors;
      transitions: {
        fast: string;
        medium: string;
        slow: string;
      };
      spacing: {
        section: string;
        page: string;
      };
    };
  }
  interface ThemeOptions {
    custom?: {
      colors?: typeof colors;
      transitions?: {
        fast: string;
        medium: string;
        slow: string;
      };
      spacing?: {
        section: string;
        page: string;
      };
    };
  }
}

export default theme; 